import type { FC } from 'react';
import React, { useContext } from 'react';
import { styled } from '@compiled/react';

import { token } from '@atlaskit/tokens';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';

import { FeatureCode } from '@confluence/feature-codes/entry-points/FeatureCodeModel';

import { PageCardContext } from '../PageCardContext';
import { Card, ContentIcon, PageCardContainer, PageLink, Title, Author } from '../Items';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledCard = styled(Card)({
	display: 'flex',
	height: '50px',
	alignItems: 'center',
	padding: token('space.100'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledCardWithHover = styled(StyledCard)({
	'&:hover': {
		backgroundColor: token('color.background.selected.hovered'),
	},
});

const GridCardStyles = xcss({
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'center',
	width: '100%',
	height: '100%',
	maxHeight: '50px',
});

const Header = xcss({
	height: '100%',
	display: 'flex',
	justifyContent: 'flex-start',
	alignItems: 'center',
	gap: 'space.150',
	paddingInlineStart: 'space.050',
});

const HeaderSmallCard = xcss({
	marginBottom: 'space.100',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IconContainer = styled.div({
	color: token('color.icon.subtle'),
});

const TitlesContainer = xcss({
	overflow: 'hidden',
	overflowWrap: 'break-word',
	display: 'flex',
	flexDirection: 'column',
	gap: 'space.050',
});

const TitlesContainerSmallCard = xcss({
	whiteSpace: 'nowrap',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TitleContainer = styled.div({
	display: '-webkit-box',
	WebkitLineClamp: '2',
	WebkitBoxOrient: 'vertical',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
});

type CompactEndOfPageViewProps = {
	showCardsAsListitem?: boolean;
};

export const CompactEndOfPageView: FC<CompactEndOfPageViewProps> = ({
	showCardsAsListitem = true,
}) => {
	// ⚠️ WARNING: Do you need to add conditional logic? Please refer to README#Views ⚠️
	const { smallCard } = useContext(PageCardContext);

	return (
		<PageCardContainer
			StyledCard={smallCard ? StyledCardWithHover : StyledCard}
			isListitem={showCardsAsListitem}
		>
			<PageLink featureAttributionCode={FeatureCode.EOP_REC} />
			<Box xcss={GridCardStyles} testId="page-card-end-of-page-view">
				<Box xcss={[Header, ...(smallCard ? [HeaderSmallCard] : [])]}>
					<IconContainer>
						<ContentIcon />
					</IconContainer>
					<Box xcss={[TitlesContainer, ...(smallCard ? [TitlesContainerSmallCard] : [])]}>
						<TitleContainer>
							<Title />
						</TitleContainer>
						<Author />
					</Box>
				</Box>
			</Box>
		</PageCardContainer>
	);
};
